"use client"

import { useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { Close as CloseIcon, CloudUpload as UploadIcon } from "@mui/icons-material"
import FilePreview from "./FilePreview"

export default function NewPostDialog({ open, onClose, onSave, category, providerId }) {
 
  const [formData, setFormData] = useState({
    subject_line: "",
    input_text: "",
    feed_show_status: true,
    startDay: null,
    endDay: null,
    welcomeStartDay: null,
    welcomeEndDay: null,
    type: '', // Remove 's' from the end
  })
  const [files, setFiles] = useState({
    photo: null,
    picture_1: null,
    picture_2: null,
  })
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleDateChange = (field) => (date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date?.toISOString() || null,
    }))
  }

  const handleFileChange = (e) => {
    const { name, files: uploadedFiles } = e.target
    if (uploadedFiles.length > 0) {
      setFiles((prev) => ({
        ...prev,
        [name]: uploadedFiles[0],
      }))
    }
  }

  const removeFile = (name) => {
    setFiles((prev) => ({
      ...prev,
      [name]: null,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const formDataToSend = new FormData()
      formDataToSend.append("provider", providerId)
      formDataToSend.append("type", category.slice(0, -1))
      formDataToSend.append("subject_line", formData.subject_line)
      formDataToSend.append("input_text", formData.input_text)
      formDataToSend.append("feed_show_status", formData.feed_show_status)
      formDataToSend.append("startDay", formData.startDay)
      formDataToSend.append("endDay", formData.endDay)
      formDataToSend.append("welcomeStartDay", formData.startDay)
      formDataToSend.append("welcomeEndDay", formData.endDay)

      Object.entries(files).forEach(([key, file]) => {
        if (file) {
          formDataToSend.append(key, file)
        }
      })

      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/create_post`, {
        method: "POST",
        body: formDataToSend,
      })

      if (response.ok) {
        const data = await response.json()
        setFormData({
          subject_line: "",
          input_text: "",
          feed_show_status: true,
          startDay: null,
          endDay: null,
          welcomeStartDay: null,
          welcomeEndDay: null,
          type: '', // Remove 's' from the end
        })
        onSave()
        onClose()
      } else {
        throw new Error("Failed to create post")
      }
    } catch (error) {
      console.error("Error creating post:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">New {category.slice(0, -1)}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3}>
            <TextField
              label="Subject Line"
              name="subject_line"
              value={formData.subject_line}
              onChange={handleChange}
              fullWidth
              required
            />

            <TextField
              label="Description"
              name="input_text"
              value={formData.input_text}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              required
            />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Media Files
              </Typography>
              <Stack direction="row" spacing={2} mb={2}>
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                  Main Photo
                  <input type="file" hidden name="photo" onChange={handleFileChange}  accept="image/*,video/*,audio/*,application/pdf" />
                </Button>
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                  Additional Photo 1
                  <input
                    type="file"
                    hidden
                    name="picture_1"
                    onChange={handleFileChange}
                    accept="image/*,video/*,audio/*,application/pdf"
                  />
                </Button>
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                  Additional Photo 2
                  <input
                    type="file"
                    hidden
                    name="picture_2"
                    onChange={handleFileChange}
                   accept="image/*,video/*,audio/*,application/pdf"
                  />
                </Button>
              </Stack>
              <Stack direction="row" spacing={2}>
                {Object.entries(files).map(
                  ([key, file]) => file && <FilePreview key={key} file={file} onRemove={() => removeFile(key)} previewUrl={
                    file instanceof File ? URL.createObjectURL(file) : file.url
                  } />,
                )}
              </Stack>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction="row" spacing={2}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDay ? new Date(formData.startDay) : null}
                  onChange={handleDateChange("startDay")}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={formData.endDay ? new Date(formData.endDay) : null}
                  onChange={handleDateChange("endDay")}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <DatePicker
                  label="Welcome Start Date"
                  value={formData.welcomeStartDay ? new Date(formData.welcomeStartDay) : null}
                  onChange={handleDateChange('welcomeStartDay')}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
                <DatePicker
                  label="Welcome End Date"
                  value={formData.welcomeEndDay ? new Date(formData.welcomeEndDay) : null}
                  onChange={handleDateChange('welcomeEndDay')}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Stack>
            </LocalizationProvider>

            <FormControlLabel
              control={<Switch checked={formData.feed_show_status} onChange={handleChange} name="feed_show_status" />}
              label="Show in Feed"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

